import { refreshTokenResponseSchema } from '@easyflow/shared';
import { getSessionIdleStore } from '@easyflow/ui-frontend';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import { apiClient } from './api';
import { persistSessionPolicy } from './sessionPolicyStorage';
import type { SessionPolicy } from '@easyflow/shared';

/**
 * Rinnovo JWT e cleanup di logout del frontend tenant.
 *
 * L'orologio di inattività NON vive qui: è il motore condiviso `session-idle`
 * di `@easyflow/ui-frontend` (scope `tenant`, avviato da AuthProvider) a
 * decidere quando chiamare `refreshToken` e quando far scadere la sessione.
 * Questo modulo si limita a: refresh single-flight con retry per rotazione
 * concorrente, revoca del refresh token, beacon di logout alla chiusura tab.
 */

const REFRESH_RETRY_DELAY_MS = 2000;
const REFRESH_TIMEOUT_MS = 10000;

export const TOKEN_REFRESHED_EVENT = 'auth:token-refreshed';
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

/** Detail di `auth:token-refreshed`: nuovo access token + policy di sessione (se il server la manda). */
export interface TokenRefreshedDetail {
  readonly token: string;
  readonly sessionPolicy?: SessionPolicy;
}

/**
 * Esito del refresh.
 * - `refreshed`: nuovo access token salvato e propagato (`auth:token-refreshed`).
 * - `transient`: errore temporaneo (rete, timeout, 5xx, body malformato): la
 *   sessione resta valida, si ritenta al prossimo tick / alla prossima 401.
 * - `expired`: 401 definitivo (anche dopo retry per rotazione concorrente) o
 *   nessuna credenziale di refresh: la sessione è terminata lato server.
 *   Con un refresh token presente viene emesso `auth:session-expired`.
 */
export type RefreshOutcome =
  | { readonly kind: 'refreshed'; readonly token: string }
  | { readonly kind: 'transient'; readonly reason: 'network' | 'timeout' | 'server' | 'malformed' }
  | { readonly kind: 'expired' };

interface StoredAuthUser {
  readonly tenantSlug: string | null;
  readonly isMaster: boolean;
}

function readStoredUser(): StoredAuthUser | null {
  const raw = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const record: Record<string, unknown> = { ...parsed };
  const tenantSlug = typeof record.tenantSlug === 'string' && record.tenantSlug ? record.tenantSlug : null;
  const isMaster = record.role === 'super_admin' || record.is_master_tenant === true;
  return { tenantSlug, isMaster };
}

interface AuthEndpoints {
  readonly refresh: string;
  readonly logout: string;
}

function resolveAuthEndpoints(user: StoredAuthUser | null): AuthEndpoints {
  if (user?.isMaster) {
    return { refresh: '/api/master/refresh-token', logout: '/api/master/logout' };
  }
  const slug = user?.tenantSlug || localStorage.getItem(AUTH_STORAGE_KEYS.TENANT_SLUG) || 'demo';
  return { refresh: `/api/${slug}/refresh-token`, logout: `/api/${slug}/logout` };
}

/** La richiesta di refresh non deve trattenere indefinitamente tutte le API in attesa del single-flight. */
function fetchRefresh(endpoint: string, refreshTokenValue: string): Promise<Response> {
  return fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: refreshTokenValue }),
    signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
  });
}

function classifyFetchError(error: unknown): RefreshOutcome {
  const isTimeout = error instanceof DOMException && (error.name === 'TimeoutError' || error.name === 'AbortError');
  return { kind: 'transient', reason: isTimeout ? 'timeout' : 'network' };
}

/**
 * Revoca il refresh token lato server (idempotente: con storage già pulito è un no-op).
 * Gli errori di rete sono ignorati: lo storage locale viene comunque azzerato dal chiamante.
 */
export async function performLogoutCleanup(): Promise<void> {
  const storedRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  const user = readStoredUser();
  if (!storedRefreshToken || !user) return;
  const { logout } = resolveAuthEndpoints(user);
  try {
    await fetch(logout, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: storedRefreshToken }),
    });
  } catch {
    // best effort: la revoca fallita non blocca il logout locale
  }
}

async function applyRefreshResponse(response: Response): Promise<RefreshOutcome> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'transient', reason: 'malformed' };
  }
  const parsed = refreshTokenResponseSchema.safeParse(body);
  if (!parsed.success) {
    return { kind: 'transient', reason: 'malformed' };
  }
  const { token, refreshToken: rotatedRefreshToken, sessionPolicy } = parsed.data.data;

  localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
  if (rotatedRefreshToken) {
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, rotatedRefreshToken);
  }
  if (sessionPolicy) {
    persistSessionPolicy(sessionPolicy);
  }
  apiClient.setToken(token);

  const detail: TokenRefreshedDetail = sessionPolicy ? { token, sessionPolicy } : { token };
  window.dispatchEvent(new CustomEvent<TokenRefreshedDetail>(TOKEN_REFRESHED_EVENT, { detail }));
  return { kind: 'refreshed', token };
}

function expireSession(): RefreshOutcome {
  localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  return { kind: 'expired' };
}

async function doRefreshRequest(): Promise<RefreshOutcome> {
  const currentRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  if (!currentRefreshToken) {
    return { kind: 'expired' };
  }
  const endpoint = resolveAuthEndpoints(readStoredUser()).refresh;

  let response: Response;
  try {
    response = await fetchRefresh(endpoint, currentRefreshToken);
  } catch (error) {
    return classifyFetchError(error);
  }

  if (response.ok) {
    return applyRefreshResponse(response);
  }
  if (response.status !== 401) {
    return { kind: 'transient', reason: 'server' };
  }

  // 401: può essere una rotazione concorrente (un'altra tab ha già consumato
  // questo refresh token). Breve attesa, poi si riparte dallo storage.
  await new Promise<void>((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));

  const retryRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  if (!retryRefreshToken) {
    // Un'altra tab ha già chiuso la sessione.
    return expireSession();
  }
  if (retryRefreshToken !== currentRefreshToken) {
    const rotatedAccessToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    if (rotatedAccessToken) {
      apiClient.setToken(rotatedAccessToken);
      return { kind: 'refreshed', token: rotatedAccessToken };
    }
  }

  let retryResponse: Response;
  try {
    retryResponse = await fetchRefresh(endpoint, retryRefreshToken);
  } catch (error) {
    return classifyFetchError(error);
  }
  if (retryResponse.ok) {
    return applyRefreshResponse(retryResponse);
  }
  if (retryResponse.status === 401) {
    return expireSession();
  }
  return { kind: 'transient', reason: 'server' };
}

let refreshPromise: Promise<RefreshOutcome> | null = null;

/** Refresh single-flight: le chiamate concorrenti condividono la stessa richiesta. Non rigetta mai. */
export function refreshToken(): Promise<RefreshOutcome> {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = doRefreshRequest().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

function isReloadNavigation(): boolean {
  if (typeof PerformanceNavigationTiming === 'undefined') return false;
  const [entry] = performance.getEntriesByType('navigation');
  return entry instanceof PerformanceNavigationTiming && entry.type === 'reload';
}

/** Deadline di inattività superata senza che il tick l'abbia ancora consumata (tab sospesa poi chiusa). */
function isIdleDeadlinePassed(): boolean {
  const store = getSessionIdleStore('tenant');
  if (store.getStatus() === 'expired') return true;
  const deadline = store.getDeadline();
  return deadline !== null && deadline <= Date.now();
}

let pageUnloadHandlerRegistered = false;

/**
 * Alla chiusura della tab con sessione già scaduta per inattività revoca il
 * refresh token via `sendBeacon` (la pagina non può più attendere una fetch).
 */
export function initPageUnloadHandler(): void {
  if (pageUnloadHandlerRegistered) return;
  pageUnloadHandlerRegistered = true;

  window.addEventListener('pagehide', (event: PageTransitionEvent) => {
    if (event.persisted || isReloadNavigation()) return;
    if (!isIdleDeadlinePassed()) return;

    const storedRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    const user = readStoredUser();
    if (!storedRefreshToken || !user) return;
    const { logout } = resolveAuthEndpoints(user);
    const blob = new Blob([JSON.stringify({ refreshToken: storedRefreshToken })], { type: 'application/json' });
    navigator.sendBeacon(logout, blob);
  });
}
